import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Package, Plus, Pencil, Trash2, FolderPlus, Upload, Download, AlertTriangle, Search as SearchIcon, Layers, FileText, Printer, TrendingUp, TrendingDown, Equal, History, FileSpreadsheet, BarChart3, Trash } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MagazzinoPage() {
  const [tab, setTab] = useState('materiali');
  const [categorie, setCategorie] = useState([]);
  const [materiali, setMateriali] = useState([]);
  const [q, setQ] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [onlyLowStock, setOnlyLowStock] = useState(false);
  const [loading, setLoading] = useState(false);

  const [matDialogOpen, setMatDialogOpen] = useState(false);
  const [editingMat, setEditingMat] = useState(null);
  const [matForm, setMatForm] = useState({ nome: '', descrizione: '', categoria_id: '', codice_interno: '', unita: 'pz', qty_current: 0, qty_min: 0, prezzo: 0, location: '', note: '' });

  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({ nome: '', colore: '#64748b', note: '' });

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [importBusy, setImportBusy] = useState(false);
  const [labelBusy, setLabelBusy] = useState(false);

  const [movDialogOpen, setMovDialogOpen] = useState(false);
  const [movMat, setMovMat] = useState(null);
  const [movForm, setMovForm] = useState({ tipo: 'carico', quantita: '', note: '', cantiere_id: '' });
  const [movBusy, setMovBusy] = useState(false);
  const [cantieriList, setCantieriList] = useState([]);

  // Storico movimenti (iter100c)
  const [storico, setStorico] = useState([]);
  const [storicoLoading, setStoricoLoading] = useState(false);
  const [storicoExporting, setStoricoExporting] = useState(false);
  const [storicoFilters, setStoricoFilters] = useState({ materiale_id: '', tipo: '', user_email: '', date_from: '', date_to: '' });
  const [workersList, setWorkersList] = useState([]);

  // Analisi top usati (iter100k)
  const [analisi, setAnalisi] = useState([]);
  const [analisiLoading, setAnalisiLoading] = useState(false);
  const [analisiDays, setAnalisiDays] = useState(90);

  const fetchAnalisi = useCallback(async () => {
    setAnalisiLoading(true);
    try {
      const r = await axios.get(`${API}/magazzino/analytics/top-usati`, { params: { days: analisiDays, limit: 25 }, withCredentials: true });
      setAnalisi(r.data?.items || []);
    } catch (err) { toast.error('Errore caricamento analisi'); }
    finally { setAnalisiLoading(false); }
  }, [analisiDays]);

  useEffect(() => {
    if (tab === 'analisi') fetchAnalisi();
  }, [tab, fetchAnalisi]);

  const fetchStorico = useCallback(async () => {
    setStoricoLoading(true);
    try {
      const params = {};
      Object.entries(storicoFilters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await axios.get(`${API}/magazzino/movimenti`, { params, withCredentials: true });
      setStorico(r.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento storico');
    } finally { setStoricoLoading(false); }
  }, [storicoFilters]);

  useEffect(() => {
    if (tab !== 'storico') return;
    fetchStorico();
    // Carica anche la lista operai per il filtro utente (una tantum)
    if (workersList.length === 0) {
      axios.get(`${API}/users?role=operaio`, { withCredentials: true })
        .then(r => setWorkersList(r.data || []))
        .catch(() => { /* silenzio: se non è admin, il filtro utente resta libero */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, storicoFilters]);

  const exportStoricoExcel = async () => {
    setStoricoExporting(true);
    try {
      const params = {};
      Object.entries(storicoFilters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await axios.get(`${API}/magazzino/movimenti/export-excel`, { params, withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `storico-magazzino-${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`Esportati ${storico.length} movimenti`);
    } catch (err) { toast.error('Errore esportazione Excel'); }
    finally { setStoricoExporting(false); }
  };

  const openMovimento = (m, tipo = 'carico') => { setMovMat(m); setMovForm({ tipo, quantita: '', note: '', cantiere_id: '' }); setMovDialogOpen(true); };
  const saveMovimento = async () => {
    const qta = parseFloat(movForm.quantita);
    if (!qta || qta <= 0) { toast.error('Inserisci una quantità > 0'); return; }
    setMovBusy(true);
    try {
      await axios.post(`${API}/magazzino/movimenti`, {
        materiale_id: movMat.id,
        tipo: movForm.tipo,
        quantita: qta,
        cantiere_id: movForm.cantiere_id || '',
        note: movForm.note || '',
      }, { withCredentials: true });
      toast.success(movForm.tipo === 'carico' ? 'Carico registrato' : movForm.tipo === 'scarico' ? 'Scarico registrato' : 'Giacenza rettificata');
      setMovDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore movimento');
    } finally { setMovBusy(false); }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, mats] = await Promise.all([
        axios.get(`${API}/magazzino/categorie`, { withCredentials: true }),
        axios.get(`${API}/magazzino/materiali`, { params: { low_stock: onlyLowStock }, withCredentials: true }),
      ]);
      setCategorie(cats.data || []);
      setMateriali(mats.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, [onlyLowStock]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Carica lista cantieri una tantum (serve per il dialog scarico)
  useEffect(() => {
    axios.get(`${API}/cantieri`, { withCredentials: true })
      .then(r => setCantieriList(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setCantieriList([]));
  }, []);

  // iter100n: debounce ricerca magazzino
  const [qPending, setQPending] = useState(false);
  useEffect(() => {
    if (!q) { fetchAll(); return; }
    setQPending(true);
    const t = setTimeout(async () => {
      if (q.trim().length < 2) { setQPending(false); return; }
      try {
        const r = await axios.get(`${API}/magazzino/materiali/search`, { params: { q }, withCredentials: true });
        setMateriali(r.data?.items || []);
      } catch { /* silenzioso */ }
      finally { setQPending(false); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const doSearch = async (val) => {
    setQ(val);
    // il fetch avviene nel useEffect debounce sopra
  };

  const openNewMat = () => { setEditingMat(null); setMatForm({ nome: '', descrizione: '', categoria_id: '', codice_interno: '', unita: 'pz', qty_current: 0, qty_min: 0, prezzo: 0, location: '', note: '' }); setMatDialogOpen(true); setNewMatTab('catalogo'); setCatSearch(''); setCatResults([]); setCatSelected(new Map()); };

  // iter100m: picker dal Catalogo dentro il dialog "Nuovo materiale"
  const [newMatTab, setNewMatTab] = useState('catalogo');
  const [catSearch, setCatSearch] = useState('');
  const [catResults, setCatResults] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  // Map<catalogo_id, {catalogo, qty_initial, qty_min, location}>
  const [catSelected, setCatSelected] = useState(new Map());
  const [catImportBusy, setCatImportBusy] = useState(false);

  const searchCatalogo = useCallback(async (q) => {
    setCatLoading(true);
    try {
      const params = new URLSearchParams({ limit: '30' });
      if (q && q.trim()) params.set('search', q.trim());
      const r = await axios.get(`${API}/materiali-catalogo/list?${params}`, { withCredentials: true });
      setCatResults(r.data || []);
    } catch { setCatResults([]); }
    finally { setCatLoading(false); }
  }, []);

  // debounce ricerca
  useEffect(() => {
    if (!matDialogOpen || newMatTab !== 'catalogo') return;
    const t = setTimeout(() => searchCatalogo(catSearch), 250);
    return () => clearTimeout(t);
  }, [catSearch, matDialogOpen, newMatTab, searchCatalogo]);

  const toggleCatSelect = (it) => {
    const next = new Map(catSelected);
    if (next.has(it.id)) next.delete(it.id);
    else next.set(it.id, { catalogo: it, qty_initial: 0, qty_min: 0, location: '' });
    setCatSelected(next);
  };

  const updateCatSelected = (id, field, value) => {
    const next = new Map(catSelected);
    const row = next.get(id);
    if (row) { row[field] = value; next.set(id, row); setCatSelected(next); }
  };

  const submitCatImport = async () => {
    if (catSelected.size === 0) { toast.info('Seleziona almeno un articolo'); return; }
    setCatImportBusy(true);
    try {
      const items = Array.from(catSelected.values()).map(v => ({
        catalogo_id: v.catalogo.id,
        qty_initial: parseFloat(v.qty_initial) || 0,
        qty_min: parseFloat(v.qty_min) || 0,
        location: v.location || '',
      }));
      const r = await axios.post(`${API}/magazzino/materiali/import-catalogo-selected`, { items }, { withCredentials: true, timeout: 120000 });
      const { created, updated, skipped } = r.data;
      toast.success(`Aggiunti al Magazzino: ${created} nuovi, ${updated} aggiornati${skipped ? `, ${skipped} saltati` : ''}`);
      setMatDialogOpen(false);
      setCatSelected(new Map());
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore aggiunta dal Catalogo');
    } finally { setCatImportBusy(false); }
  };
  const openEditMat = (m) => { setEditingMat(m); setMatForm({ ...m }); setMatDialogOpen(true); };
  const saveMat = async () => {
    try {
      if (editingMat) await axios.put(`${API}/magazzino/materiali/${editingMat.id}`, matForm, { withCredentials: true });
      else await axios.post(`${API}/magazzino/materiali`, matForm, { withCredentials: true });
      toast.success('Salvato'); setMatDialogOpen(false); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const deleteMat = async (m) => {
    if (!window.confirm(`Eliminare "${m.nome}"?`)) return;
    try { await axios.delete(`${API}/magazzino/materiali/${m.id}`, { withCredentials: true }); toast.success('Eliminato'); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const openNewCat = () => { setEditingCat(null); setCatForm({ nome: '', colore: '#64748b', note: '' }); setCatDialogOpen(true); };
  const openEditCat = (c) => { setEditingCat(c); setCatForm({ ...c }); setCatDialogOpen(true); };
  const saveCat = async () => {
    try {
      if (editingCat) await axios.put(`${API}/magazzino/categorie/${editingCat.id}`, catForm, { withCredentials: true });
      else await axios.post(`${API}/magazzino/categorie`, catForm, { withCredentials: true });
      toast.success('Categoria salvata'); setCatDialogOpen(false); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const deleteCat = async (c) => {
    if (!window.confirm(`Eliminare "${c.nome}"?`)) return;
    try { await axios.delete(`${API}/magazzino/categorie/${c.id}`, { withCredentials: true }); toast.success('Eliminata'); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const importCSV = async (file) => {
    setImportBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/magazzino/materiali/import-csv`, fd, { withCredentials: true });
      toast.success(`Import: ${r.data.created} creati, ${r.data.skipped} saltati`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore import'); }
    finally { setImportBusy(false); }
  };

  const importFromCatalogo = async () => {
    if (!window.confirm('Importare TUTTI i materiali dal Catalogo con giacenza 0?')) return;
    setImportBusy(true);
    try {
      const r = await axios.post(`${API}/magazzino/materiali/import-catalogo`, {}, { withCredentials: true });
      toast.success(`Importati ${r.data.created} materiali dal catalogo`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setImportBusy(false); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === displayed.length && displayed.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayed.map(m => m.id)));
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminare ${selectedIds.size} materiali selezionati?\nL'operazione è reversibile solo tramite database.`)) return;
    try {
      const r = await axios.post(`${API}/magazzino/materiali/bulk-delete`, { ids: Array.from(selectedIds) }, { withCredentials: true });
      toast.success(`Eliminati ${r.data.deleted} materiali`);
      setSelectedIds(new Set());
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore eliminazione multipla'); }
  };

  const deleteAll = async () => {
    if (materiali.length === 0) { toast.info('Nessun materiale da eliminare'); return; }
    const confirmText = window.prompt(`Stai per eliminare TUTTI i ${materiali.length} materiali del magazzino.\n\nDigita ELIMINA per confermare:`);
    if (confirmText !== 'ELIMINA') { toast.info('Operazione annullata'); return; }
    try {
      const r = await axios.delete(`${API}/magazzino/materiali`, { params: { confirm: 'YES' }, withCredentials: true });
      toast.success(`Eliminati ${r.data.deleted} materiali`);
      setSelectedIds(new Set());
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore eliminazione totale'); }
  };

  const printLabels = async () => {
    if (selectedIds.size === 0) { toast.error('Seleziona almeno 1 materiale'); return; }
    setLabelBusy(true);
    try {
      const r = await axios.post(`${API}/magazzino/materiali/labels-pdf`, { materiali_ids: Array.from(selectedIds), per_riga: 3, per_colonna: 8 }, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      // Rilascia il blob URL dopo 60s (il tab aperto lo tiene in cache in memoria)
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error('Errore generazione PDF'); }
    finally { setLabelBusy(false); }
  };

  const displayed = materiali.filter(m => filterCat === 'all' || m.categoria_id === filterCat);
  const totalValue = displayed.reduce((s, m) => s + (Number(m.qty_current) || 0) * (Number(m.prezzo) || 0), 0);
  const lowCount = materiali.filter(m => (m.qty_current || 0) < (m.qty_min || 0)).length;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="magazzino-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />Magazzino
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Gestione materiali, categorie, prelievi e stampa etichette</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-slate-200"><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase">Materiali</p><p className="text-2xl font-bold text-slate-900">{materiali.length}</p></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase">Categorie</p><p className="text-2xl font-bold text-blue-600">{categorie.length}</p></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase">Sotto scorta</p><p className="text-2xl font-bold text-red-600 flex items-center gap-1">{lowCount > 0 && <AlertTriangle className="h-5 w-5" />}{lowCount}</p></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase">Valore magazzino</p><p className="text-2xl font-bold text-emerald-600">€ {totalValue.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p></CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="materiali" data-testid="tab-materiali"><Package className="h-4 w-4 mr-1.5" />Materiali</TabsTrigger>
          <TabsTrigger value="categorie" data-testid="tab-categorie"><Layers className="h-4 w-4 mr-1.5" />Categorie</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import"><Upload className="h-4 w-4 mr-1.5" />Importa</TabsTrigger>
          <TabsTrigger value="storico" data-testid="tab-storico"><History className="h-4 w-4 mr-1.5" />Storico</TabsTrigger>
          <TabsTrigger value="analisi" data-testid="tab-analisi"><BarChart3 className="h-4 w-4 mr-1.5" />Analisi</TabsTrigger>
        </TabsList>

        <TabsContent value="materiali" className="space-y-3">
          <Card className="border-slate-200">
            <CardContent className="p-3 flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[240px]">
                <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={q} onChange={(e) => doSearch(e.target.value)} placeholder="Cerca (es. 'cavo 2.5' o scansiona barcode)" className="pl-8" data-testid="mag-search" />
              </div>
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger className="w-[180px]" data-testid="mag-filter-cat"><SelectValue placeholder="Categoria" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  {categorie.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant={onlyLowStock ? 'default' : 'outline'} size="sm" onClick={() => setOnlyLowStock(v => !v)} data-testid="mag-toggle-low">
                <AlertTriangle className="h-4 w-4 mr-1.5" />Solo sotto scorta
              </Button>
              <Button size="sm" onClick={openNewMat} className="bg-slate-900 text-white" data-testid="mag-new-mat"><Plus className="h-4 w-4 mr-1.5" />Nuovo materiale</Button>
              <Button size="sm" variant="outline" onClick={printLabels} disabled={labelBusy || selectedIds.size === 0} data-testid="mag-print-labels"><Printer className="h-4 w-4 mr-1.5" />Stampa etichette ({selectedIds.size})</Button>
              <Button size="sm" variant="outline" onClick={deleteAll} disabled={materiali.length === 0} className="text-red-700 border-red-300 hover:bg-red-50" data-testid="mag-delete-all"><Trash className="h-4 w-4 mr-1.5" />Elimina tutti</Button>
            </CardContent>
          </Card>

          {/* Barra bulk actions (iter100k) */}
          {selectedIds.size > 0 && (
            <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-md px-4 py-2" data-testid="mag-bulk-bar">
              <div className="text-sm text-red-800"><b>{selectedIds.size}</b> materiali selezionati</div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())}>Annulla selezione</Button>
                <Button size="sm" onClick={bulkDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="mag-bulk-delete-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina {selectedIds.size}
                </Button>
              </div>
            </div>
          )}

          <Card className="border-slate-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="w-6 pl-3"><input type="checkbox" checked={displayed.length > 0 && selectedIds.size === displayed.length} onChange={toggleSelectAll} data-testid="mag-check-all" /></TableHead>
                  <TableHead className="text-xs uppercase">Nome</TableHead>
                  <TableHead className="text-xs uppercase">Barcode</TableHead>
                  <TableHead className="text-xs uppercase">Categoria</TableHead>
                  <TableHead className="text-xs uppercase text-right">Giacenza</TableHead>
                  <TableHead className="text-xs uppercase text-right">Prezzo</TableHead>
                  <TableHead className="text-xs uppercase">Location</TableHead>
                  <TableHead className="text-xs uppercase text-right">Azioni</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {loading ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
                    : displayed.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500"><Package className="h-8 w-8 mx-auto text-slate-300 mb-2" />Nessun materiale — crea o importa</TableCell></TableRow>
                    : displayed.map(m => {
                      const cat = categorie.find(c => c.id === m.categoria_id);
                      const low = (m.qty_current || 0) < (m.qty_min || 0);
                      return (
                        <TableRow key={m.id} className="border-slate-100" data-testid={`mag-row-${m.id}`}>
                          <TableCell className="pl-3"><input type="checkbox" checked={selectedIds.has(m.id)} onChange={() => toggleSelect(m.id)} data-testid={`mag-check-${m.id}`} /></TableCell>
                          <TableCell className="font-medium">{m.nome}<div className="text-[10px] text-slate-400">{m.descrizione}</div></TableCell>
                          <TableCell className="font-mono text-xs text-blue-700">{m.barcode}</TableCell>
                          <TableCell>{cat ? <Badge variant="outline" style={{ background: (cat.colore || '#64748b') + '20', color: cat.colore, borderColor: cat.colore + '40' }}>{cat.nome}</Badge> : <span className="text-slate-400 text-xs">—</span>}</TableCell>
                          <TableCell className="text-right"><span className={low ? 'text-red-600 font-bold' : 'text-slate-900 font-medium'}>{m.qty_current} {m.unita}</span>{m.qty_min > 0 && <div className="text-[10px] text-slate-400">min {m.qty_min}</div>}</TableCell>
                          <TableCell className="text-right text-slate-700">€ {(Number(m.prezzo) || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-xs text-slate-500">{m.location || '—'}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600 hover:bg-emerald-50" onClick={() => openMovimento(m, 'carico')} title="Carico (aggiungi giacenza)" data-testid={`mag-carico-${m.id}`}><TrendingUp className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-600 hover:bg-amber-50" onClick={() => openMovimento(m, 'scarico')} title="Scarico (rimuovi giacenza)" data-testid={`mag-scarico-${m.id}`}><TrendingDown className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600 hover:bg-blue-50" onClick={() => openMovimento(m, 'rettifica')} title="Rettifica (imposta valore esatto)" data-testid={`mag-rettifica-${m.id}`}><Equal className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditMat(m)} title="Modifica dati anagrafici"><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteMat(m)} title="Elimina"><Trash2 className="h-3.5 w-3.5" /></Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categorie" className="space-y-3">
          <Card className="border-slate-200"><CardContent className="p-3"><Button size="sm" onClick={openNewCat} className="bg-slate-900 text-white" data-testid="mag-new-cat"><FolderPlus className="h-4 w-4 mr-1.5" />Nuova categoria</Button></CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-0">
            <Table><TableHeader><TableRow className="bg-slate-50"><TableHead>Nome</TableHead><TableHead>Colore</TableHead><TableHead>Note</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
              <TableBody>
                {categorie.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">Nessuna categoria</TableCell></TableRow>
                  : categorie.map(c => (
                    <TableRow key={c.id}><TableCell className="font-medium">{c.nome}</TableCell><TableCell><span className="inline-block w-6 h-6 rounded" style={{ background: c.colore }} /></TableCell><TableCell className="text-xs text-slate-500">{c.note}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditCat(c)}><Pencil className="h-3.5 w-3.5" /></Button><Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteCat(c)}><Trash2 className="h-3.5 w-3.5" /></Button></TableCell></TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="import" className="space-y-3">
          <Card className="border-slate-200"><CardContent className="p-4 space-y-4">
            <div>
              <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><FileText className="h-4 w-4" />Import CSV/Excel</h3>
              <p className="text-xs text-slate-500 mb-2">Colonne accettate: nome, descrizione, categoria, unita, qty_current, qty_min, prezzo, location, codice_interno. Categorie mancanti create automaticamente.</p>
              <Input type="file" accept=".csv,.xlsx" onChange={(e) => { const f = e.target.files?.[0]; if (f) importCSV(f); }} disabled={importBusy} data-testid="mag-import-csv-file" />
            </div>
            <div className="pt-4 border-t border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><Download className="h-4 w-4" />Importa da Catalogo Materiali esistente</h3>
              <p className="text-xs text-slate-500 mb-2">Copia tutti i materiali del catalogo attuale nel magazzino con giacenza 0.</p>
              <Button size="sm" onClick={importFromCatalogo} disabled={importBusy} className="bg-blue-600 text-white" data-testid="mag-import-catalogo"><Download className="h-4 w-4 mr-1.5" />Importa dal Catalogo</Button>
            </div>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="storico" className="space-y-3">
          <Card className="border-slate-200">
            <CardContent className="p-3 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
                <div className="space-y-1">
                  <Label className="text-[11px] text-slate-500">Materiale</Label>
                  <Select value={storicoFilters.materiale_id || 'all'} onValueChange={(v) => setStoricoFilters(f => ({ ...f, materiale_id: v === 'all' ? '' : v }))}>
                    <SelectTrigger data-testid="storico-filter-mat"><SelectValue placeholder="Tutti" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i materiali</SelectItem>
                      {materiali.map(m => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-slate-500">Tipo</Label>
                  <Select value={storicoFilters.tipo || 'all'} onValueChange={(v) => setStoricoFilters(f => ({ ...f, tipo: v === 'all' ? '' : v }))}>
                    <SelectTrigger data-testid="storico-filter-tipo"><SelectValue placeholder="Tutti" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      <SelectItem value="carico">Carico</SelectItem>
                      <SelectItem value="scarico">Scarico</SelectItem>
                      <SelectItem value="rettifica">Rettifica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-slate-500">Operatore</Label>
                  <Select value={storicoFilters.user_email || 'all'} onValueChange={(v) => setStoricoFilters(f => ({ ...f, user_email: v === 'all' ? '' : v }))}>
                    <SelectTrigger data-testid="storico-filter-user"><SelectValue placeholder="Tutti" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti gli operatori</SelectItem>
                      {workersList.map(w => <SelectItem key={w.email} value={w.email}>{w.name} ({w.email})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-slate-500">Dal</Label>
                  <Input type="date" value={storicoFilters.date_from} onChange={(e) => setStoricoFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="storico-filter-date-from" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-slate-500">Al</Label>
                  <Input type="date" value={storicoFilters.date_to} onChange={(e) => setStoricoFilters(f => ({ ...f, date_to: e.target.value }))} data-testid="storico-filter-date-to" />
                </div>
              </div>
              <div className="flex justify-between items-center pt-1 border-t border-slate-100">
                <div className="text-xs text-slate-500">{storicoLoading ? 'Caricamento…' : `${storico.length} movimenti trovati`}</div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setStoricoFilters({ materiale_id: '', tipo: '', user_email: '', date_from: '', date_to: '' })} data-testid="storico-clear-filters">Reset filtri</Button>
                  <Button size="sm" onClick={exportStoricoExcel} disabled={storicoExporting || storico.length === 0} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="storico-export-excel">
                    <FileSpreadsheet className="h-4 w-4 mr-1.5" />
                    {storicoExporting ? 'Esportazione…' : 'Esporta Excel'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase">Data/Ora</TableHead>
                  <TableHead className="text-xs uppercase">Tipo</TableHead>
                  <TableHead className="text-xs uppercase">Materiale</TableHead>
                  <TableHead className="text-xs uppercase text-right">Qta</TableHead>
                  <TableHead className="text-xs uppercase text-right hidden md:table-cell">Costo</TableHead>
                  <TableHead className="text-xs uppercase hidden md:table-cell">Cantiere</TableHead>
                  <TableHead className="text-xs uppercase">Operatore</TableHead>
                  <TableHead className="text-xs uppercase hidden lg:table-cell">Note</TableHead>
                  <TableHead className="text-xs uppercase text-right">DDT</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {storicoLoading ? <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Caricamento…</TableCell></TableRow>
                    : storico.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-500"><History className="h-8 w-8 mx-auto text-slate-300 mb-2" />Nessun movimento con questi filtri</TableCell></TableRow>
                    : storico.map(m => {
                      let dateStr = '';
                      try {
                        const dt = new Date(m.created_at);
                        dateStr = dt.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                      } catch { dateStr = (m.created_at || '').slice(0, 16); }
                      const tipoStyle = m.tipo === 'carico' ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                        : m.tipo === 'scarico' ? 'bg-amber-100 text-amber-700 border-amber-200'
                        : 'bg-blue-100 text-blue-700 border-blue-200';
                      const TipoIcon = m.tipo === 'carico' ? TrendingUp : m.tipo === 'scarico' ? TrendingDown : Equal;
                      return (
                        <TableRow key={m.id} className="border-slate-100" data-testid={`storico-row-${m.id}`}>
                          <TableCell className="text-xs whitespace-nowrap">{dateStr}</TableCell>
                          <TableCell><Badge variant="outline" className={tipoStyle}><TipoIcon className="h-3 w-3 mr-1" />{m.tipo}</Badge></TableCell>
                          <TableCell className="font-medium text-slate-800">{m.materiale_nome}</TableCell>
                          <TableCell className="text-right font-mono">{m.quantita}</TableCell>
                          <TableCell className="text-right text-xs text-slate-700 hidden md:table-cell font-medium">{m.costo_totale ? `€ ${Number(m.costo_totale).toFixed(2)}` : '—'}</TableCell>
                          <TableCell className="text-xs hidden md:table-cell"><span className="text-slate-700">{m.cantiere_nome || <span className="text-slate-300">—</span>}</span></TableCell>
                          <TableCell className="text-xs"><div className="font-medium text-slate-700">{m.user_name || '—'}</div><div className="text-[10px] text-slate-400">{m.user_email}</div></TableCell>
                          <TableCell className="text-xs text-slate-500 hidden lg:table-cell max-w-[200px] truncate" title={m.note}>{m.note || '—'}</TableCell>
                          <TableCell className="text-right">
                            {m.tipo === 'scarico' && m.cantiere_id && m.costo_totale > 0 ? (
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-purple-700 hover:bg-purple-50" title="Scarica DDT PDF" onClick={() => window.open(`${API}/magazzino/movimenti/${m.id}/ddt-pdf`, '_blank')} data-testid={`storico-ddt-${m.id}`}>
                                <FileText className="h-3.5 w-3.5" />
                              </Button>
                            ) : (
                              <span className="text-[10px] text-slate-300">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analisi" className="space-y-3" data-testid="tab-analisi-content">
          <Card className="border-slate-200">
            <CardContent className="p-3 flex flex-wrap items-center gap-2">
              <div className="text-sm text-slate-600">
                <BarChart3 className="h-4 w-4 inline mr-1.5 text-blue-600" />
                Top materiali più scaricati negli ultimi
              </div>
              <Select value={String(analisiDays)} onValueChange={(v) => setAnalisiDays(parseInt(v))}>
                <SelectTrigger className="w-[140px]" data-testid="analisi-periodo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 giorni</SelectItem>
                  <SelectItem value="90">90 giorni</SelectItem>
                  <SelectItem value="180">6 mesi</SelectItem>
                  <SelectItem value="365">1 anno</SelectItem>
                  <SelectItem value="3650">Tutto lo storico</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-500">{analisiLoading ? 'Caricamento…' : `${analisi.length} materiali`}</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="w-10 text-xs uppercase">#</TableHead>
                  <TableHead className="text-xs uppercase">Materiale</TableHead>
                  <TableHead className="text-xs uppercase text-right">Q.tà scaricata</TableHead>
                  <TableHead className="text-xs uppercase text-right hidden md:table-cell">N° movimenti</TableHead>
                  <TableHead className="text-xs uppercase text-right hidden md:table-cell">Costo cumulato</TableHead>
                  <TableHead className="text-xs uppercase text-right hidden lg:table-cell">Giacenza</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {analisiLoading ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Caricamento…</TableCell></TableRow>
                    : analisi.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500"><BarChart3 className="h-8 w-8 mx-auto text-slate-300 mb-2" />Nessuno scarico nel periodo selezionato</TableCell></TableRow>
                    : (() => {
                      const maxQ = Math.max(...analisi.map(a => a.quantita_totale || 0), 1);
                      return analisi.map((a, idx) => {
                        const pct = Math.round(((a.quantita_totale || 0) / maxQ) * 100);
                        return (
                          <TableRow key={a.materiale_id} className="border-slate-100" data-testid={`analisi-row-${a.materiale_id}`}>
                            <TableCell className="text-slate-400 font-mono text-sm pl-4">#{idx + 1}</TableCell>
                            <TableCell>
                              <div className="font-medium text-slate-800">{a.materiale_nome}</div>
                              <div className="mt-1 h-2 bg-slate-100 rounded overflow-hidden">
                                <div className="h-full bg-blue-500 rounded" style={{ width: `${pct}%` }} />
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold text-slate-900">{a.quantita_totale} <span className="text-xs text-slate-400">{a.unita}</span></TableCell>
                            <TableCell className="text-right text-slate-700 hidden md:table-cell">{a.num_movimenti}</TableCell>
                            <TableCell className="text-right text-emerald-700 font-medium hidden md:table-cell">€ {(a.costo_totale || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-right hidden lg:table-cell">
                              <span className={a.qty_current > 0 ? 'text-slate-700' : 'text-red-600 font-medium'}>{a.qty_current} {a.unita}</span>
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Dialog Materiale */}
      <Dialog open={matDialogOpen} onOpenChange={setMatDialogOpen}>
        <DialogContent className="sm:max-w-[820px] max-h-[90vh] overflow-y-auto" data-testid="mag-mat-dialog">
          <DialogHeader><DialogTitle>{editingMat ? 'Modifica materiale' : 'Nuovo materiale in Magazzino'}</DialogTitle></DialogHeader>
          {editingMat ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1"><Label>Nome *</Label><Input value={matForm.nome} onChange={(e) => setMatForm({ ...matForm, nome: e.target.value })} data-testid="mag-form-nome" /></div>
                <div className="col-span-2 space-y-1"><Label>Descrizione</Label><Textarea value={matForm.descrizione} onChange={(e) => setMatForm({ ...matForm, descrizione: e.target.value })} rows={2} /></div>
                <div className="space-y-1"><Label>Categoria</Label><Select value={matForm.categoria_id || 'none'} onValueChange={(v) => setMatForm({ ...matForm, categoria_id: v === 'none' ? '' : v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">— Nessuna —</SelectItem>{categorie.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1"><Label>Codice interno</Label><Input value={matForm.codice_interno} onChange={(e) => setMatForm({ ...matForm, codice_interno: e.target.value })} /></div>
                <div className="space-y-1"><Label>Unità</Label><Input value={matForm.unita} onChange={(e) => setMatForm({ ...matForm, unita: e.target.value })} /></div>
                <div className="space-y-1"><Label>Location</Label><Input value={matForm.location} onChange={(e) => setMatForm({ ...matForm, location: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-slate-500">Giacenza attuale</Label><div className="text-sm text-slate-700 font-medium h-10 flex items-center border border-slate-200 rounded-md px-3 bg-slate-50">{matForm.qty_current} {matForm.unita} <span className="text-[10px] text-slate-500 ml-2">(usa i pulsanti +/− dalla riga per modificarla)</span></div></div>
                <div className="space-y-1"><Label>Scorta minima</Label><Input type="number" step="0.01" value={matForm.qty_min} onChange={(e) => setMatForm({ ...matForm, qty_min: parseFloat(e.target.value) || 0 })} /></div>
                <div className="space-y-1"><Label>Prezzo (€)</Label><Input type="number" step="0.01" value={matForm.prezzo} onChange={(e) => setMatForm({ ...matForm, prezzo: parseFloat(e.target.value) || 0 })} /></div>
                <div className="col-span-2 space-y-1"><Label>Note</Label><Textarea value={matForm.note} onChange={(e) => setMatForm({ ...matForm, note: e.target.value })} rows={2} /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setMatDialogOpen(false)}>Annulla</Button><Button onClick={saveMat} className="bg-slate-900 text-white" data-testid="mag-form-save">Salva</Button></DialogFooter>
            </>
          ) : (
            <Tabs value={newMatTab} onValueChange={setNewMatTab}>
              <TabsList className="grid grid-cols-2 w-full mb-3">
                <TabsTrigger value="catalogo" data-testid="tab-new-catalogo"><FolderPlus className="h-4 w-4 mr-1.5" />Dal Catalogo</TabsTrigger>
                <TabsTrigger value="manuale" data-testid="tab-new-manuale"><Plus className="h-4 w-4 mr-1.5" />Manuale</TabsTrigger>
              </TabsList>
              <TabsContent value="catalogo" className="space-y-3">
                <div className="relative">
                  <SearchIcon className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
                  <Input value={catSearch} onChange={(e) => setCatSearch(e.target.value)} placeholder="Cerca per codice, nome, EAN o marca..." className="pl-9" data-testid="cat-picker-search" autoFocus />
                </div>
                <div className="border border-slate-200 rounded-md max-h-[280px] overflow-y-auto">
                  {catLoading ? (
                    <div className="p-6 text-center text-slate-400 text-sm">Caricamento…</div>
                  ) : catResults.length === 0 ? (
                    <div className="p-6 text-center text-slate-400 text-sm">{catSearch ? 'Nessun risultato' : 'Digita per cercare nel Catalogo'}</div>
                  ) : catResults.map(it => {
                    const sel = catSelected.has(it.id);
                    return (
                      <div key={it.id} onClick={() => toggleCatSelect(it)} className={`flex items-start gap-3 p-2.5 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${sel ? 'bg-emerald-50/60' : ''}`} data-testid={`cat-picker-row-${it.id}`}>
                        <input type="checkbox" checked={sel} readOnly className="mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-slate-500 font-mono">{it.codice} {it.produttore && `• ${it.produttore}`} {it.ean && `• EAN ${it.ean}`}</div>
                          <div className="text-sm text-slate-800 truncate">{it.descrizione}</div>
                        </div>
                        {it.prezzo_listino > 0 && <div className="text-xs text-slate-600 font-mono whitespace-nowrap">€ {Number(it.prezzo_listino).toFixed(2)}</div>}
                      </div>
                    );
                  })}
                </div>
                {catSelected.size > 0 && (
                  <div className="border border-emerald-200 bg-emerald-50/50 rounded-md p-3 space-y-2">
                    <div className="text-sm font-medium text-emerald-900">{catSelected.size} articoli selezionati — imposta quantità:</div>
                    <div className="space-y-2 max-h-[220px] overflow-y-auto">
                      {Array.from(catSelected.entries()).map(([id, row]) => (
                        <div key={id} className="bg-white rounded border border-slate-200 p-2">
                          <div className="text-xs font-mono text-slate-500 mb-1 truncate">{row.catalogo.codice} — {row.catalogo.descrizione}</div>
                          <div className="grid grid-cols-3 gap-2">
                            <Input type="number" step="0.01" min="0" placeholder="Q.tà iniziale" value={row.qty_initial} onChange={(e) => updateCatSelected(id, 'qty_initial', e.target.value)} data-testid={`cat-picker-qty-${id}`} />
                            <Input type="number" step="0.01" min="0" placeholder="Scorta minima" value={row.qty_min} onChange={(e) => updateCatSelected(id, 'qty_min', e.target.value)} />
                            <Input placeholder="Posizione" value={row.location} onChange={(e) => updateCatSelected(id, 'location', e.target.value)} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setMatDialogOpen(false)} disabled={catImportBusy}>Annulla</Button>
                  <Button onClick={submitCatImport} disabled={catImportBusy || catSelected.size === 0} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="cat-picker-submit">
                    <Package className="h-4 w-4 mr-2" /> {catImportBusy ? 'Aggiunta…' : `Aggiungi ${catSelected.size} al Magazzino`}
                  </Button>
                </DialogFooter>
              </TabsContent>
              <TabsContent value="manuale">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-1"><Label>Nome *</Label><Input value={matForm.nome} onChange={(e) => setMatForm({ ...matForm, nome: e.target.value })} data-testid="mag-form-nome" /></div>
                  <div className="col-span-2 space-y-1"><Label>Descrizione</Label><Textarea value={matForm.descrizione} onChange={(e) => setMatForm({ ...matForm, descrizione: e.target.value })} rows={2} /></div>
                  <div className="space-y-1"><Label>Categoria</Label><Select value={matForm.categoria_id || 'none'} onValueChange={(v) => setMatForm({ ...matForm, categoria_id: v === 'none' ? '' : v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">— Nessuna —</SelectItem>{categorie.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-1"><Label>Codice interno</Label><Input value={matForm.codice_interno} onChange={(e) => setMatForm({ ...matForm, codice_interno: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Unità</Label><Input value={matForm.unita} onChange={(e) => setMatForm({ ...matForm, unita: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Location</Label><Input value={matForm.location} onChange={(e) => setMatForm({ ...matForm, location: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Giacenza iniziale</Label><Input type="number" step="0.01" value={matForm.qty_current} onChange={(e) => setMatForm({ ...matForm, qty_current: parseFloat(e.target.value) || 0 })} data-testid="mag-form-qty-init" /></div>
                  <div className="space-y-1"><Label>Scorta minima</Label><Input type="number" step="0.01" value={matForm.qty_min} onChange={(e) => setMatForm({ ...matForm, qty_min: parseFloat(e.target.value) || 0 })} /></div>
                  <div className="space-y-1"><Label>Prezzo (€)</Label><Input type="number" step="0.01" value={matForm.prezzo} onChange={(e) => setMatForm({ ...matForm, prezzo: parseFloat(e.target.value) || 0 })} /></div>
                  <div className="col-span-2 space-y-1"><Label>Note</Label><Textarea value={matForm.note} onChange={(e) => setMatForm({ ...matForm, note: e.target.value })} rows={2} /></div>
                </div>
                <DialogFooter className="mt-3"><Button variant="outline" onClick={() => setMatDialogOpen(false)}>Annulla</Button><Button onClick={saveMat} className="bg-slate-900 text-white" data-testid="mag-form-save">Salva</Button></DialogFooter>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Categoria */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editingCat ? 'Modifica categoria' : 'Nuova categoria'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Nome *</Label><Input value={catForm.nome} onChange={(e) => setCatForm({ ...catForm, nome: e.target.value })} data-testid="mag-catform-nome" /></div>
            <div className="space-y-1"><Label>Colore</Label><Input type="color" value={catForm.colore} onChange={(e) => setCatForm({ ...catForm, colore: e.target.value })} className="h-10 w-24" /></div>
            <div className="space-y-1"><Label>Note</Label><Textarea value={catForm.note} onChange={(e) => setCatForm({ ...catForm, note: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCatDialogOpen(false)}>Annulla</Button><Button onClick={saveCat} className="bg-slate-900 text-white" data-testid="mag-catform-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog Movimento (carico/scarico/rettifica) */}
      <Dialog open={movDialogOpen} onOpenChange={setMovDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="mag-mov-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {movForm.tipo === 'carico' && <TrendingUp className="h-5 w-5 text-emerald-600" />}
              {movForm.tipo === 'scarico' && <TrendingDown className="h-5 w-5 text-amber-600" />}
              {movForm.tipo === 'rettifica' && <Equal className="h-5 w-5 text-blue-600" />}
              {movForm.tipo === 'carico' ? 'Carico giacenza' : movForm.tipo === 'scarico' ? 'Scarico giacenza' : 'Rettifica giacenza'}
            </DialogTitle>
          </DialogHeader>
          {movMat && (
            <div className="space-y-3">
              <div className="text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                <div className="font-medium">{movMat.nome}</div>
                <div className="text-xs text-slate-500">Giacenza attuale: <b>{movMat.qty_current} {movMat.unita}</b></div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Button type="button" variant={movForm.tipo === 'carico' ? 'default' : 'outline'} size="sm" onClick={() => setMovForm({ ...movForm, tipo: 'carico' })} className={movForm.tipo === 'carico' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}><TrendingUp className="h-3.5 w-3.5 mr-1" />Carico</Button>
                <Button type="button" variant={movForm.tipo === 'scarico' ? 'default' : 'outline'} size="sm" onClick={() => setMovForm({ ...movForm, tipo: 'scarico' })} className={movForm.tipo === 'scarico' ? 'bg-amber-600 text-white hover:bg-amber-700' : ''}><TrendingDown className="h-3.5 w-3.5 mr-1" />Scarico</Button>
                <Button type="button" variant={movForm.tipo === 'rettifica' ? 'default' : 'outline'} size="sm" onClick={() => setMovForm({ ...movForm, tipo: 'rettifica' })} className={movForm.tipo === 'rettifica' ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}><Equal className="h-3.5 w-3.5 mr-1" />Rettifica</Button>
              </div>
              <div className="space-y-1">
                <Label>{movForm.tipo === 'rettifica' ? 'Nuova giacenza esatta' : 'Quantità'} *</Label>
                <Input type="number" step="0.01" min="0" value={movForm.quantita} onChange={(e) => setMovForm({ ...movForm, quantita: e.target.value })} autoFocus placeholder={movForm.tipo === 'rettifica' ? `es. ${movMat.qty_current || 0}` : 'es. 10'} data-testid="mag-mov-qty" />
                <p className="text-[11px] text-slate-500">
                  {movForm.tipo === 'carico' && <>Verrà <b className="text-emerald-700">aggiunta</b> alla giacenza attuale.</>}
                  {movForm.tipo === 'scarico' && <>Verrà <b className="text-amber-700">sottratta</b> dalla giacenza attuale.</>}
                  {movForm.tipo === 'rettifica' && <>La giacenza sarà <b className="text-blue-700">impostata</b> a questo valore esatto.</>}
                </p>
              </div>
              {movForm.tipo === 'scarico' && (
                <div className="space-y-1">
                  <Label>Cantiere <span className="text-slate-400 text-[10px]">(opzionale, alimenta il costo materiali)</span></Label>
                  <Select value={movForm.cantiere_id || 'none'} onValueChange={(v) => setMovForm({ ...movForm, cantiere_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="mag-mov-cantiere"><SelectValue placeholder="— Nessuno (magazzino generico) —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Nessuno (magazzino generico) —</SelectItem>
                      {cantieriList.map(c => <SelectItem key={c.id} value={c.id}>{c.name || c.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {movForm.cantiere_id && movMat.prezzo > 0 && (
                    <p className="text-[11px] text-emerald-700">
                      Costo aggiunto al cantiere: <b>€ {((parseFloat(movForm.quantita) || 0) * movMat.prezzo).toFixed(2)}</b>
                    </p>
                  )}
                </div>
              )}
              <div className="space-y-1">
                <Label>Note (opzionale)</Label>
                <Textarea value={movForm.note} onChange={(e) => setMovForm({ ...movForm, note: e.target.value })} rows={2} placeholder="Es: fornitore Rexel, DDT n. 123..." />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovDialogOpen(false)} disabled={movBusy}>Annulla</Button>
            <Button onClick={saveMovimento} disabled={movBusy || !movForm.quantita} className="bg-slate-900 text-white" data-testid="mag-mov-save">{movBusy ? 'Salvataggio...' : 'Conferma'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
